import React, { useState } from 'react';
import styled from 'styled-components';
import videoCarouselPreviewImg from '../../src/images/video-carousel-preview.png';
import { GatsbyImage } from 'gatsby-plugin-image';
import Video from './video';

import { Container, TitleSet, colors, media } from '../ui-components';

/**
 * Component that renders the a card containing white-paper specific insight information
 *
 * @component VideoCarousel
 * 
 * @prop {object} carouselVideos
 * @prop {string} subtitle
 * @prop {string} sectionTitle
 * @prop {boolean} hideLabel
 * @prop {boolean} isPreview
 * 
 * @returns {JSX} returns videoCarousel component 
 */
export const VideoCarousel = ({ carouselVideos, subtitle, sectionTitle, isPreview }) => {
  const initialVideo = carouselVideos[0]?.videoUrl;
  const [currentVideo, setCurrentVideo] = useState(initialVideo);

  if (isPreview) {
    return (
      <Container sectionContainer>
        <TitleSet label={sectionTitle} title={subtitle} />
        <VideoCarouselPreview src={videoCarouselPreviewImg} alt={sectionTitle || 'Credera Stories'} />
      </Container>
    );
  }

  return (
    <Container id={'video-carousel'} sectionContainer>
      <TitleSet label={sectionTitle ? sectionTitle : ''} title={subtitle} />
      <CarouselContainer>
        <VideoCarouselEmbedContainer>
          <VideoCarouselEmbed
            title={subtitle}
            embedUrl={currentVideo + '?modestbranding=1&rel=0&cc_load_policy=1'}
            allowFullScreen
          />
        </VideoCarouselEmbedContainer>
        <RelatedVideos>
          <VideoList>
            {carouselVideos.map((video, index) => (
              <VideoCard
                key={index}
                onClick={() => setCurrentVideo(video.videoUrl)}
                isCurrentVideo={video.videoUrl === currentVideo}
              >
                {video?.videoThumbnail?.gatsbyImageData && <Thumbnail image={video.videoThumbnail.gatsbyImageData} alt={video.title} />}
                <div>
                  <p>{video.title}</p>
                  <span>{video.date}</span>
                </div>
              </VideoCard>
            ))}
          </VideoList>
        </RelatedVideos>
      </CarouselContainer>
    </Container>
  );
};

const CarouselContainer = styled.div`
  display: flex;
  justify-content: space-between;
  max-height: 420px;
  gap: 24px;
  width: 100%;

  @media screen and (max-width: ${media.mediumTop}) {
    flex-direction: column;
    align-items: center;
    gap: 0px;
    max-height: none;
  }
`;

const RelatedVideos = styled.div`
  max-width: 39.8%;
  width: 100%;

  h5 {
    font-family: 'Lato', sans-serif;
    font-weight: 900;
    margin-bottom: 10px;
  }

  @media screen and (max-width: ${media.mediumTop}) {
    margin-top: 40px;
    max-width: 100%;
  }
`;

const VideoCarouselEmbedContainer = styled.div`
  width: 100%;

  @media screen and (max-width: ${media.mediumTop}) {
    padding-bottom: 56.25%;
    position: relative;
  }
`;

const VideoCarouselEmbed = styled(Video)`
  margin-top: 0;
  height: 420px;
  width: 100%;

  @media screen and (max-width: ${media.mediumTop}) {
    height: 100%;
    max-width: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const VideoList = styled.div`
  border: 1px solid ${colors.gray3};
  max-height: 420px;
  overflow-y: auto;

  ::-webkit-scrollbar {
    display: none;
  }
  scrollbar-width: none;

  @media screen and (max-width: ${media.mediumTop}) {
    width: 100%;
  }
  @media screen and (max-width: ${media.mediumTop}) and (min-width: ${media.mediumBottom}) {
    padding-top: 0;
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    max-height: 496px;
  }
`;

const VideoCard = styled.div`
  display: flex;
  cursor: pointer;
  padding: 12px;
  ${({ isCurrentVideo }) => (isCurrentVideo ? `background-color: ${colors.buttonHover}` : '')};

  @media screen and (max-width: ${media.mediumTop}) and (min-width: ${media.mediumBottom}) {
    display: flex;
  }

  div {
    flex: 50%;
  }

  img {
    margin-bottom: 0;
  }

  p {
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    margin: 0 0 4px;
    color: ${({ isCurrentVideo }) => (isCurrentVideo ? `${colors.pureWhite}` : `${colors.midnightBlack}`)};

    @media screen and (max-width: ${media.mediumBottom}) {
      margin: 0px;
    }
  }

  span {
    display: block;
    color: ${({ isCurrentVideo }) => (isCurrentVideo ? `${colors.pureWhite}` : `${colors.gray1}`)};
    font-size: 14px;
    line-height: 24px;

    @media screen and (max-width: ${media.mediumBottom}) {
      font-weight: 400px;
    }
  }
`;

const Thumbnail = styled(GatsbyImage)`
  background-image: ${({ src }) => `url("${src}");`};
  background-repeat: no-repeat;
  background-position: center center;
  margin-right: 20px;
  height: 80px;
  max-width: 123px;
`;

const VideoCarouselPreview = styled.img`
  padding-top: 40px;
`
